import { ENCRES, OUTILS, type Encre, type Outil } from './palette';

/**
 * **La barre d'outils du tableau — `CDC-2` F-22.**
 *
 * **La densité de cette surface est « minimale, presque nulle »** (`DESIGN.md`) : « tout
 * ce qui n'est pas le tracé **prend de la place sur le tracé**, et la latence du trait est
 * le critère numéro un ».
 *
 * **Les cinq encres sont la langue du CONTENU**, et elles ne paraissent que sous deux
 * formes : « **un tracé ou une pastille ronde pleine dans la barre d'outils** ». C'est
 * cette seconde forme. **Aucune n'est violette** : « un trait violet sur le tableau ne peut
 * donc être que l'interface ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LES DOUZE OUTILS DE F-22 Y SONT TOUS DEPUIS LE 20260827 — D-115.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Cet en-tête écrivait « les formes, la sélection, annuler et refaire, le zoom **ne sont
 * pas encore là** ; F-22 en ferme la liste à douze, quatre y sont ». **Six manquaient**, et
 * la raison n'était pas un lot en retard : le moteur écrit à la main ne les avait pas, là
 * où D-001 avait choisi un moteur qui les porte.
 *
 * **Les douze se comptent en trois familles, et la barre les tient toutes** — c'est
 * pourquoi `OUTILS` n'en liste que neuf sans que trois manquent :
 *
 * | Famille | Combien | Où |
 * |---|---|---|
 * | outils qui s'arment | **9** | `OUTILS` : crayon, surligneur, gomme, sélection, texte, trait, rectangle, cercle, flèche, pointeur temporaire, pense-bête |
 * | réglages | **2** | le choix de couleur et le choix d'épaisseur, ci-dessous |
 * | gestes ponctuels | **3** | annuler, refaire, tout effacer |
 *
 * **Le zoom et le mode main ne comptent pas dans les douze** : « ils sont acquis par
 * D-007 », donc le moteur les fournit et la barre n'a pas à les porter.
 *
 * **Annuler et refaire sont locaux à leur auteur, et c'est la règle et non une limite** :
 * « l'annulation ne porte que sur ses propres gestes » (Q349), et « si un élève efface
 * tout, **lui seul peut le défaire** ». L'historique du moteur est par poste : le corpus
 * et l'outil disent la même chose.
 */
type Props = {
  readonly outil: Outil;
  readonly encre: Encre;
  readonly epaisseur: number;
  readonly gele: boolean;
  readonly onOutil: (outil: Outil) => void;
  readonly onEncre: (encre: Encre) => void;
  readonly onEpaisseur: (epaisseur: number) => void;
  readonly onToutEffacer: () => void;
  /** « Annuler et refaire » — F-22. Local à son auteur, Q349. */
  readonly onAnnuler: () => void;
  readonly onRefaire: () => void;
};

const NOM_DE_L_OUTIL: Record<Outil, string> = {
  crayon: 'Crayon',
  surligneur: 'Surligneur',
  gomme: 'Gomme',
  selection: 'Sélection',
  texte: 'Texte',
  trait: 'Trait',
  rectangle: 'Rectangle',
  cercle: 'Cercle',
  fleche: 'Flèche',
  pointeur: 'Pointeur',
  'pense-bete': 'Pense-bête',
};

const FOND_DE_L_ENCRE: Record<Encre, string> = {
  noir: 'bg-encre-noir',
  bleu: 'bg-encre-bleu',
  rouge: 'bg-encre-rouge',
  vert: 'bg-encre-vert',
  surligneur: 'bg-encre-surligneur',
};

export function BarreDOutils({
  outil,
  encre,
  epaisseur,
  gele,
  onOutil,
  onEncre,
  onEpaisseur,
  onToutEffacer,
  onAnnuler,
  onRefaire,
}: Props) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-4 bg-fond-surface px-3 py-2 shadow-2">
      <div role="group" aria-label="Outil" className="flex gap-1">
        {OUTILS.map((valeur) => (
          <button
            key={valeur}
            type="button"
            aria-pressed={outil === valeur}
            disabled={gele}
            onClick={() => onOutil(valeur)}
            className={[
              'min-h-cible rounded-2 px-3 text-14 font-weight-700',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              outil === valeur
                ? 'bg-action-faible-fond text-action-faible-texte'
                : 'text-texte hover:bg-fond-bloc',
            ].join(' ')}
          >
            {NOM_DE_L_OUTIL[valeur]}
          </button>
        ))}
      </div>

      <div role="group" aria-label="Encre" className="flex items-center gap-2">
        {ENCRES.map((valeur) => (
          <button
            key={valeur}
            type="button"
            aria-label={valeur}
            aria-pressed={encre === valeur}
            disabled={gele}
            onClick={() => onEncre(valeur)}
            className={[
              // La cible reste à 44 points même si la pastille est petite : « aucune
              // cible en dessous de 44, le tactile étant ouvert des deux côtés ».
              'grid size-cible place-items-center rounded-2',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              encre === valeur ? 'bg-fond-bloc' : 'hover:bg-fond-attenue',
            ].join(' ')}
          >
            <span
              className={`block size-4 rounded-plein ${FOND_DE_L_ENCRE[valeur]}`}
            />
          </button>
        ))}
      </div>

      <label className="flex items-center gap-2 text-13 text-texte-faible">
        Épaisseur
        {/* **En continu, aucune liste fermée** (Q351) : un `range` natif, encapsulé
            et non remplacé. */}
        <input
          type="range"
          min={1}
          max={24}
          step={1}
          value={epaisseur}
          disabled={gele}
          onChange={(evenement) => onEpaisseur(Number(evenement.target.value))}
          className="h-cible accent-[var(--action)]"
        />
      </label>

      {/* ── Les trois gestes ponctuels : ils s'exercent, ils ne s'arment pas ── */}
      <div role="group" aria-label="Gestes" className="ml-auto flex items-center gap-1">
        {/* **Annuler ne défait que SES gestes** (Q349), et le moteur le tient par
            construction : son historique est local au poste. Aucun rôle à passer. */}
        <button
          type="button"
          disabled={gele}
          onClick={onAnnuler}
          className="min-h-cible rounded-2 px-3 text-14 font-weight-700 text-texte hover:bg-fond-bloc disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          type="button"
          disabled={gele}
          onClick={onRefaire}
          className="min-h-cible rounded-2 px-3 text-14 font-weight-700 text-texte hover:bg-fond-bloc disabled:opacity-50"
        >
          Refaire
        </button>
        {/* **« N'importe quel présent exerce le tout effacer »** (Q348), et « tout le
            monde efface le tracé de tout le monde » (Q350). Ce bouton ne prend donc aucun
            rôle, et il n'y a nulle part où en passer un. Il ne porte pas de rouge : « une
            couleur porte un niveau, un mot porte une identité » — c'est le libellé qui
            porte la gravité. */}
        <button
          type="button"
          disabled={gele}
          onClick={onToutEffacer}
          className="min-h-cible rounded-2 px-3 text-14 font-weight-700 text-texte hover:bg-fond-bloc disabled:opacity-50"
        >
          Tout effacer
        </button>
      </div>
    </div>
  );
}
